(() => {
  'use strict';
  const theme = window.theme;
  if (!theme || theme.settings?.themeName !== 'Impulse' || theme.settings?.themeVersion !== '10.0.0') return;
  if (theme.llCartStability) return;

  // The Shopify preview toolbar uses the top layer and can cover the drawer
  // checkout button. Reserve its visible wrapper height in preview sessions.
  const previewInset = () => {
    if (!document.getElementById('PBarNextFrameWrapper') &&
        !document.querySelector('script[src*="/preview-bar/"]')) return;
    let wrapper = null;
    let inset = -1;
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(() => sync()) : null;
    const sync = () => {
      const current = document.getElementById('PBarNextFrameWrapper');
      if (current !== wrapper) {
        resize?.disconnect();
        wrapper = current;
        if (wrapper) resize?.observe(wrapper);
      }
      const rect = wrapper?.getBoundingClientRect();
      const height = rect && rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight
        ? Math.max(0, Math.ceil(rect.height)) : 0;
      if (height === inset) return;
      inset = height;
      document.documentElement.style.setProperty('--ll-preview-bar-height', height + 'px');
    };
    new MutationObserver(sync).observe(document.body, {childList: true, subtree: true});
    window.addEventListener('resize', sync, {passive: true});
    sync();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', previewInset, {once: true});
  } else {
    previewInset();
  }
  const messages = theme.strings?.llCartStability || {};
  const feedback = (instance, message, error = false) => {
    let status = instance.form.querySelector('[data-ll-cart-status]');
    if (!status) {
      status = document.createElement('p');
      status.setAttribute('data-ll-cart-status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.className = 'll-cart-status';
      instance.form.append(status);
    }
    status.textContent = message;
    status.classList.toggle('ll-cart-status--error', error);
    status.hidden = !message;
  };
  const pending = (instance, value) => {
    instance.cartItemsUpdated = value;
    instance.form.setAttribute('aria-busy', String(value));
    instance.form.querySelectorAll('.js-qty__adjust, .js-qty__num, [name="checkout"]').forEach(control => {
      if (value && !control.disabled) {
        control.setAttribute('data-ll-cart-pending', '');
        control.disabled = true;
      } else if (!value && control.hasAttribute('data-ll-cart-pending')) {
        control.removeAttribute('data-ll-cart-pending');
        control.disabled = false;
      }
    });
  };
  const syncPageEmptyState = (instance, itemCount) => {
    if (instance.form.id !== 'CartPageForm') return;
    const header = instance.form.closest('.page-content')?.querySelector('.section-header');
    const copy = header?.querySelector('.rte');
    if (!copy) return;
    const empty = itemCount === 0;
    let notice = copy.querySelector('[data-ll-cart-empty]');
    if (!notice && empty) {
      notice = document.createElement('p');
      notice.setAttribute('data-ll-cart-empty', '');
      notice.setAttribute('role', 'status');
      notice.textContent = theme.strings.cartEmpty;
      copy.prepend(notice);
    }
    if (notice) notice.hidden = !empty;
    instance.form.hidden = empty;
    header.classList.toggle('section-header--404', empty);
  };
  const patch = CartForm => {
    const prototype = CartForm?.prototype;
    if (!prototype || ['init', 'onSubmit', 'buildCart', 'quantityChanged', 'cartMarkup', '_parseProductHTML'].some(method => typeof prototype[method] !== 'function')) return;
    const init = prototype.init;
    prototype.init = function() {
      init.call(this);
      this.form.addEventListener('click', event => {
        const remove = event.target.closest('[data-ll-cart-remove]');
        if (!remove || !this.form.contains(remove)) return;
        const key = remove.closest('.cart__item')?.dataset.key;
        if (!key) return;
        event.preventDefault();
        if (this.cartItemsUpdated) return;
        Promise.resolve(this.quantityChanged({detail: [key, 0, remove]})).then(() => {
          if (remove.isConnected) return;
          const focusTarget = this.form.hidden
            ? this.form.closest('.page-content')?.querySelector('.section-header a')
            : this.form.querySelector('[data-ll-cart-remove]') || this.form.querySelector('.js-drawer-close') || this.form.querySelector('[name="checkout"]');
          focusTarget?.focus();
        });
      });
    };
    const submit = prototype.onSubmit;
    prototype.onSubmit = function(event) {
      if (this.cartItemsUpdated) {
        event.preventDefault();
        this.submitBtn?.classList.remove('btn--loading');
        return false;
      }
      return submit.call(this, event);
    };
    prototype.buildCart = function() {
      const revision = this.llCartRenderRevision = (this.llCartRenderRevision || 0) + 1;
      return theme.cart.getCartProductMarkup().then(text => {
        if (revision !== this.llCartRenderRevision) return true;
        if (!this._parseProductHTML(text).items) throw new Error('Cart markup unavailable');
        this.cartMarkup(text);
        if (this.cartItemsUpdated) pending(this, true);
        return true;
      }).catch(() => {
        feedback(this, messages.refreshError || 'Your bag could not refresh. Reload to check it before trying again.', true);
        return false;
      });
    };
    prototype.quantityChanged = function(event) {
      const key = event.detail?.[0];
      const quantity = Number(event.detail?.[1]);
      const control = event.detail?.[2];
      if (!key || !Number.isInteger(quantity) || quantity < 0 || this.cartItemsUpdated) return;
      this.llCartRenderRevision = (this.llCartRenderRevision || 0) + 1;
      pending(this, true);
      feedback(this, messages.updating || 'Updating your bagâ€¦');
      control?.classList.add('is-loading');
      return Promise.resolve().then(() => theme.cart.changeItem(key, quantity)).then(raw => {
        const cart = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (cart?.status === 422) {
          feedback(this, cart.message || cart.description || messages.quantityError || 'That quantity is not available.', true);
          return this.buildCart();
        }
        if (!Array.isArray(cart?.items)) throw new Error('Cart response unavailable');
        this.wrapper.classList.toggle('is-empty', cart.item_count === 0);
        this.updateCount(cart.item_count);
        this.updateSubtotal(cart.total_price);
        syncPageEmptyState(this, cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', {detail: {cart}}));
        return this.buildCart().then(rendered => {
          if (rendered) feedback(this, '');
        });
      }).catch(() => {
        feedback(this, messages.refreshError || 'Your bag could not refresh. Reload to check it before trying again.', true);
      }).finally(() => {
        pending(this, false);
        control?.classList.remove('is-loading');
      });
    };
    theme.llCartStability = {version: '10.0.0', installed: true};
  };
  if (theme.CartForm) {
    patch(theme.CartForm);
    return;
  }
  Object.defineProperty(theme, 'CartForm', {
    configurable: true,
    enumerable: true,
    get: () => undefined,
    set: CartForm => {
      Object.defineProperty(theme, 'CartForm', {value: CartForm, configurable: true, enumerable: true, writable: true});
      patch(CartForm);
    }
  });
})();
